import React, { useState } from 'react';
import './Settings.css';

const Settings = ({
  regEmail,
  tgBound,
  tgNotBound,
  tgIdLabel,
  bindTg,
  unbindTg,
  onChangePassword,
  is2faEnabled,
  onEnable2fa,
  onConfirmEnable2fa,
  onDisable2fa,
  onRequestEmailChange,
  onConfirmEmailChange,
  logout,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [twofaStep, setTwofaStep] = useState('idle'); // 'idle' | 'enable_pending' | 'disable_pending'
  const [twofaCode, setTwofaCode] = useState('');
  const [twofaMessage, setTwofaMessage] = useState('');
  const [twofaError, setTwofaError] = useState('');
  const [disablePassword, setDisablePassword] = useState('');

  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // --- Смена email ---
  const [emailStep, setEmailStep] = useState('idle'); // 'idle' | 'code_pending'
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== newPasswordConfirm) {
      setMessage('Новые пароли не совпадают');
      return;
    }
    if (newPassword.length < 6) {
      setMessage('Пароль должен содержать минимум 6 символов');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      await onChangePassword(oldPassword, newPassword);
      setMessage('Пароль успешно изменён');
      setOldPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
    } catch (err) {
      setMessage(err.message || 'Ошибка смены пароля');
    } finally {
      setLoading(false);
    }
  };

  // --- 2FA ---
  const handleEnable2fa = async () => {
    setTwofaError('');
    setTwofaMessage('');
    setLoading(true);
    try {
      await onEnable2fa();
      setTwofaStep('enable_pending');
      setTwofaMessage('Код подтверждения отправлен на ваш email');
    } catch (err) {
      setTwofaError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEnable = async () => {
    if (!twofaCode || twofaCode.length !== 6) {
      setTwofaError('Введите 6-значный код');
      return;
    }
    setLoading(true);
    setTwofaError('');
    try {
      await onConfirmEnable2fa(twofaCode);
      setTwofaMessage('2FA успешно включена');
      setTwofaStep('idle');
      setTwofaCode('');
    } catch (err) {
      setTwofaError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2fa = async () => {
    if (!disablePassword) {
      setTwofaError('Введите текущий пароль для отключения 2FA');
      return;
    }
    setLoading(true);
    setTwofaError('');
    try {
      await onDisable2fa(disablePassword);
      setTwofaMessage('2FA отключена');
      setDisablePassword('');
      setTwofaStep('idle');
    } catch (err) {
      setTwofaError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancel2faAction = () => {
    setTwofaStep('idle');
    setTwofaCode('');
    setTwofaMessage('');
    setTwofaError('');
    setDisablePassword('');
  };

  // --- Смена email ---
  const handleRequestEmailChange = async () => {
    if (typeof onRequestEmailChange !== 'function') {
      setEmailError('Функция смены email не подключена');
      return;
    }
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setEmailError('Введите корректный email');
      return;
    }
    if (email === (regEmail || '').toLowerCase()) {
      setEmailError('Новый email совпадает с текущим');
      return;
    }
    setEmailLoading(true);
    setEmailError('');
    setEmailMessage('');
    try {
      await onRequestEmailChange(email);
      setEmailStep('code_pending');
      setEmailMessage('Код подтверждения отправлен на новую почту');
    } catch (err) {
      setEmailError(err.message || 'Ошибка запроса смены email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    if (typeof onConfirmEmailChange !== 'function') {
      setEmailError('Функция подтверждения email не подключена');
      return;
    }
    if (!emailCode || emailCode.length < 4) {
      setEmailError('Введите код подтверждения');
      return;
    }
    setEmailLoading(true);
    setEmailError('');
    try {
      await onConfirmEmailChange(newEmail.trim().toLowerCase(), emailCode.trim());
      setEmailMessage('Email успешно изменён');
      setEmailStep('idle');
      setNewEmail('');
      setEmailCode('');
    } catch (err) {
      setEmailError(err.message || 'Ошибка подтверждения');
    } finally {
      setEmailLoading(false);
    }
  };

  const cancelEmailChange = () => {
    setEmailStep('idle');
    setNewEmail('');
    setEmailCode('');
    setEmailMessage('');
    setEmailError('');
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    if (typeof logout === 'function') logout();
  };

  return (
    <div className="settings-grid">
      <div className="settings-card">
        <div className="settings-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <circle cx="12" cy="8" r="3.5" />
            <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
          </svg>
          Аккаунт
        </div>
        <div className="settings-account-info">
          Email: <b>{regEmail}</b><br />
          Telegram: <b>{tgIdLabel}</b><br />
          2FA: <b>{is2faEnabled ? 'Включена' : 'Отключена'}</b>
        </div>
        <button
          onClick={() => setShowLogoutModal(true)}
          className="settings-logout-btn"
          style={{
            marginTop: '20px',
            background: '#b33',
            borderColor: '#b33',
            color: '#fff'
          }}
        >
          Выйти из аккаунта
        </button>
      </div>

      {/* Карточка Telegram */}
      <div className="settings-card settings-telegram">
        <div>
          <div className="settings-card-title" style={{ marginBottom: '6px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7" strokeLinecap="round">
              <path d="M9 15l6-6M8 13l-2 2a3 3 0 0 0 4 4l2-2M16 11l2-2a3 3 0 0 0-4-4l-2 2" />
            </svg>
            Telegram
          </div>
          {tgBound ? (
            <div style={{ fontSize: '13px', color: '#4aae88' }}>✓ Telegram привязан</div>
          ) : (
            <div style={{ fontSize: '13px', color: '#f0a35e' }}>Не привязан — управление из бота недоступно</div>
          )}
        </div>
        {tgBound ? (
          <span className="tg-bound">✅</span>
        ) : (
          <button
            onClick={bindTg}
            className="settings-telegram-btn primary"
          >
            Привязать
          </button>
        )}
      </div>

      <div className="settings-card">
        <div className="settings-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7" strokeLinejoin="round">
            <path d="M12 3l7 3v5c0 4.2-2.9 7.4-7 8.5-4.1-1.1-7-4.3-7-8.5V6l7-3z" />
          </svg>
          Двухфакторная аутентификация (2FA)
        </div>
        <p className="settings-2fa-text">
          Статус: <b style={{ color: is2faEnabled ? '#4aae88' : '#c8d0d9' }}>{is2faEnabled ? 'Включена' : 'Отключена'}</b>
        </p>
        {twofaMessage && <div style={{ color: '#4aae88', fontSize: '14px', marginBottom: '8px' }}>{twofaMessage}</div>}
        {twofaError && <div style={{ color: '#ff6b6b', fontSize: '14px', marginBottom: '8px' }}>{twofaError}</div>}

        {twofaStep === 'idle' && (
          <div>
            {!is2faEnabled ? (
              <button onClick={handleEnable2fa} className="settings-2fa-btn" disabled={loading}>
                {loading ? 'Загрузка...' : 'Включить 2FA'}
              </button>
            ) : (
              <div>
                <input
                  type="password"
                  placeholder="Введите пароль для отключения"
                  className="settings-password-input"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                />
                <button onClick={handleDisable2fa} className="settings-2fa-btn" style={{ background: '#b33', borderColor: '#b33' }} disabled={loading}>
                  {loading ? 'Загрузка...' : 'Отключить 2FA'}
                </button>
              </div>
            )}
          </div>
        )}

        {twofaStep === 'enable_pending' && (
          <div>
            <p style={{ fontSize: '13px', color: '#c8d0d9' }}>Введите код, отправленный на email:</p>
            <input
              inputMode="numeric"
              maxLength="6"
              placeholder="6-значный код"
              className="settings-password-input"
              value={twofaCode}
              onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, ''))}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={handleConfirmEnable} className="settings-2fa-btn" disabled={loading}>
                {loading ? 'Загрузка...' : 'Подтвердить'}
              </button>
              <button onClick={cancel2faAction} className="settings-2fa-btn" style={{ background: '#444', borderColor: '#555' }}>
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Смена email */}
      <div className="settings-card">
        <div className="settings-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <path d="M4 6h16v12H4z" />
            <path d="M4 6l8 7 8-7" />
          </svg>
          Сменить email
        </div>
        <p className="settings-2fa-text" style={{ marginBottom: '12px' }}>
          Текущий: <b>{regEmail}</b>
        </p>
        {emailMessage && (
          <div style={{ color: '#4aae88', fontSize: '14px', marginBottom: '8px' }}>{emailMessage}</div>
        )}
        {emailError && (
          <div style={{ color: '#ff6b6b', fontSize: '14px', marginBottom: '8px' }}>{emailError}</div>
        )}

        {emailStep === 'idle' && (
          <div>
            <input
              type="email"
              placeholder="Новый email"
              className="settings-password-input"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
            <button
              onClick={handleRequestEmailChange}
              className="settings-2fa-btn"
              disabled={emailLoading}
            >
              {emailLoading ? 'Отправка...' : 'Отправить код'}
            </button>
          </div>
        )}

        {emailStep === 'code_pending' && (
          <div>
            <p style={{ fontSize: '13px', color: '#c8d0d9', marginBottom: '8px' }}>
              Код отправлен на <b>{newEmail}</b>
            </p>
            <input
              inputMode="numeric"
              maxLength="8"
              placeholder="Код подтверждения"
              className="settings-password-input"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button
                onClick={handleConfirmEmailChange}
                className="settings-2fa-btn"
                disabled={emailLoading}
              >
                {emailLoading ? 'Проверка...' : 'Подтвердить'}
              </button>
              <button
                onClick={cancelEmailChange}
                className="settings-2fa-btn"
                style={{ background: '#444', borderColor: '#555' }}
              >
                Отмена
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Смена пароля */}
      <div className="settings-card">
        <div className="settings-card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7">
            <rect x="4" y="10" width="16" height="10" rx="2.5" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          Сменить пароль
        </div>
        <input
          type="password"
          placeholder="Текущий пароль"
          className="settings-password-input"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
        />
        <input
          type="password"
          placeholder="Новый пароль (мин. 6 символов)"
          className="settings-password-input"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <input
          type="password"
          placeholder="Подтвердите новый пароль"
          className="settings-password-input"
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
        />
        {message && (
          <div style={{ color: message.includes('успешно') ? '#4aae88' : '#ff6b6b', fontSize: '14px', marginBottom: '10px' }}>
            {message}
          </div>
        )}
        <button
          className="settings-password-save"
          onClick={handleChangePassword}
          disabled={loading}
        >
          {loading ? 'Сохранение...' : 'Сохранить пароль'}
        </button>
      </div>

      {showLogoutModal && (
        <div className="logout-modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', color: '#fff' }}>Выход из аккаунта</h3>
            <p style={{ color: '#c8d0d9', margin: '0 0 20px' }}>
              Вы уверены, что хотите выйти?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="logout-modal-btn cancel"
                onClick={() => setShowLogoutModal(false)}
              >
                Отмена
              </button>
              <button
                className="logout-modal-btn confirm"
                onClick={confirmLogout}
              >
                Да, выйти
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;